import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { adminAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Settings, Save, Loader2, Brain, Gauge, Sliders, Shield, Globe, BarChart3, KeyRound, Mic, Trash2, Copy } from 'lucide-react';

// ExpoTalk keeps its own configuration (database). Missing value = the feature returns a clear 503 error.
const CONFIG_GROUPS = {
  transcription: {
    icon: Mic,
    keys: ['processing.transcription_provider', 'llm.gemini_api_key', 'processing.assemblyai_api_key',
      'ai.transcription_model', 'ai.summary_model', 'limits.daily_transcriptions'],
  },
  security: {
    icon: Shield,
    keys: ['security.cors_origins', 'auth.jwt_expiry_hours', 'auth.min_password_length'],
  },
  models: {
    icon: Brain,
    keys: ['ai.chunk_analysis_model', 'ai.translation_model', 'ai.chat_model', 'ai.live_translation_model',
      'ai.session_analysis_model', 'ai.max_tokens', 'ai.temperature'],
  },
  limits: {
    icon: Gauge,
    keys: ['limits.max_upload_size_mb', 'limits.max_meetings_list', 'limits.max_search_results', 'limits.max_live_sessions',
      'limits.chat_context_meetings', 'limits.search_default_limit', 'limits.session_history_display'],
  },
  processing: {
    icon: Sliders,
    keys: ['processing.max_retries', 'processing.retry_delay_seconds', 'processing.chunk_min_bytes',
      'processing.chunk_duration_minutes', 'processing.analysis_interval_seconds', 'processing.silence_delay_ms'],
  },
  live: {
    icon: Globe,
    keys: ['live.default_language1', 'live.default_language2', 'live.default_person1_name', 'live.default_person2_name',
      'live.websocket_reconnect_ms'],
  },
  summary: {
    icon: BarChart3,
    keys: ['summary.detailed_threshold', 'summary.comprehensive_threshold'],
  },
  integrations: {
    icon: Globe,
    keys: ['integrations.expopulse_api_url'],
  },
};

const SELECTS = { 'processing.transcription_provider': ['gemini', 'assemblyai'] };

const ApiKeys = ({ t }) => {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState('');
  const [org, setOrg] = useState('');
  const [created, setCreated] = useState(null);
  const load = useCallback(async () => {
    try { setKeys(await adminAPI.listApiKeys()); } catch { toast.error(t('adminSettings.apiKeys.failed')); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    try {
      const r = await adminAPI.createApiKey({ name, organization_id: org });
      setCreated(r); setName(''); setOrg(''); await load();
    } catch (e) { toast.error(e.response?.data?.detail || t('adminSettings.apiKeys.failed')); }
  };
  const revoke = async (id) => {
    try { await adminAPI.revokeApiKey(id); await load(); toast.success(t('adminSettings.apiKeys.revokedOk')); }
    catch { toast.error(t('adminSettings.apiKeys.failed')); }
  };
  return (
    <Card className="rounded-2xl border-slate-200 dark:border-slate-800" data-testid="api-keys">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5" />{t('adminSettings.apiKeys.title')}</CardTitle>
        <p className="text-sm text-muted-foreground">{t('adminSettings.apiKeys.description')}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {created && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950 p-3 text-sm" data-testid="api-key-created">
            <p className="font-medium">{t('adminSettings.apiKeys.copyNow')}</p>
            <div className="flex items-center gap-2 mt-2">
              <code className="break-all">{created.key}</code>
              <Button variant="ghost" size="icon" onClick={() => navigator.clipboard.writeText(created.key)}><Copy className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-2 items-end">
          <div><Label>{t('adminSettings.apiKeys.name')}</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="w-48" data-testid="api-key-name" /></div>
          <div><Label>{t('adminSettings.apiKeys.organization')}</Label><Input value={org} onChange={(e) => setOrg(e.target.value)} className="w-80" data-testid="api-key-org" /></div>
          <Button onClick={create} disabled={!name || !org} className="bg-violet-600 hover:bg-violet-700" data-testid="api-key-create">{t('adminSettings.apiKeys.create')}</Button>
        </div>
        <Separator />
        {keys.length === 0 && <p className="text-sm text-muted-foreground">{t('adminSettings.apiKeys.none')}</p>}
        {keys.map((k) => (
          <div key={k.id} className="flex items-center gap-3 text-sm">
            <span className="font-medium w-40 truncate">{k.name}</span>
            <code className="text-xs">{k.prefix}…</code>
            <span className="text-xs text-muted-foreground truncate">org {k.organization_id}</span>
            <span className="text-xs text-muted-foreground">{t('adminSettings.apiKeys.lastUsed')}: {k.last_used_at ? k.last_used_at.slice(0, 16).replace('T', ' ') : '—'}</span>
            {k.revoked ? <Badge variant="outline">{t('adminSettings.apiKeys.revoked')}</Badge> : (
              <Button variant="ghost" size="icon" title={t('adminSettings.apiKeys.revoke')} onClick={() => revoke(k.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const AdminConfig = () => {
  const { user, t } = useApp();
  const [config, setConfig] = useState(null);
  const [edited, setEdited] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      setConfig(await adminAPI.getConfig());
      setEdited({});
    } catch (err) {
      toast.error(err.response?.status === 403 ? t('adminSettings.denied') : t('adminSettings.loadFailed'));
    }
    setLoading(false);
  }, []);
  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  // nazwy kluczy zawieraja kropki — slownik etykiet pobierany w calosci, nie sciezka t('...labels.<klucz>')
  const labels = t('adminSettings.labels');
  const label = (key) => (labels && typeof labels === 'object' && labels[key]) || key;
  const isSecret = (key) => config?.[key]?.value === '***' || key.endsWith('_api_key');
  const handleChange = (key, value) => setEdited((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    const values = {};
    for (const [key, val] of Object.entries(edited)) {
      if (val === null) { values[key] = null; continue; }           // clear
      if (val === '' && isSecret(key)) continue;                     // empty secret = no change
      const type = config[key]?.type;
      if ((type === 'int' || type === 'float') && val !== '') {
        const num = Number(val);
        if (Number.isNaN(num)) { toast.error(`${t('adminSettings.invalidNumber')}: ${label(key)}`); return; }
        values[key] = type === 'int' ? Math.trunc(num) : num;
      } else {
        values[key] = val === '' ? null : val;
      }
    }
    if (Object.keys(values).length === 0) { setEdited({}); return; }
    setSaving(true);
    try {
      const result = await adminAPI.updateConfig({ values });
      toast.success(t('adminSettings.saved'));
      await fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('adminSettings.saveFailed'));
    }
    setSaving(false);
  };

  const hasChanges = Object.keys(edited).length > 0;

  if (!user?.is_superadmin) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-background flex items-center justify-center">
        <Card className="max-w-md"><CardContent className="pt-6 text-center">
          <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          
          <p className="text-muted-foreground">{t('adminSettings.denied')}</p>
        </CardContent></Card>
      </div>
    );
  }
  if (loading || !config) {
    return <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-violet-600" /></div>;
  }

  const field = (key) => {
    const c = config[key];
    if (!c) return null;
    const secret = isSecret(key);
    const modified = key in edited;
    const value = modified ? (edited[key] ?? '') : (secret ? '' : (c.value ?? ''));
    return (
      <div className="flex items-center gap-4" key={key} data-testid={`cfg-${key}`}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Label className="text-sm font-medium">{label(key)}</Label>
            {!c.set && !modified && <Badge variant="outline" className="text-xs text-red-600 border-red-300" data-testid="not-set">{t('adminSettings.notSet')}</Badge>}
            {secret && c.set && !modified && <Badge variant="outline" className="text-xs text-emerald-700 border-emerald-300" data-testid="secret-set">{t('adminSettings.secretSet')}</Badge>}
            {modified && <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">{t('adminSettings.modified')}</Badge>}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{key}</p>
        </div>
        <div className="flex items-center gap-2">
          {SELECTS[key] ? (
            <select value={value} onChange={(e) => handleChange(key, e.target.value)} className="w-56 h-9 rounded-md border px-2 bg-background" name={key}>
              <option value="">—</option>
              {SELECTS[key].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <Input
              name={key}
              type={secret ? 'password' : (c.type === 'int' || c.type === 'float') ? 'number' : 'text'}
              autoComplete="off"
              placeholder={secret ? (c.set ? t('adminSettings.placeholderSet') : t('adminSettings.placeholderNotSet')) : ''}
              value={value}
              onChange={(e) => handleChange(key, e.target.value)}
              className={`w-56 ${modified ? 'border-amber-400 ring-1 ring-amber-200' : ''}`}
            />
          )}
          {c.set && (
            <Button variant="ghost" size="sm" title={t('adminSettings.clear')} onClick={() => handleChange(key, null)} data-testid={`clear-${key}`}>{t('adminSettings.clear')}</Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><Settings className="w-8 h-8" />{t('adminSettings.title')}</h1>
            <p className="text-muted-foreground mt-1">{t('adminSettings.subtitle')}</p>
          </div>
          <Button onClick={handleSave} disabled={!hasChanges || saving} className="bg-violet-600 hover:bg-violet-700" data-testid="save-settings">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{t('adminSettings.save')}
          </Button>
        </div>
        <div className="space-y-6">
          {Object.entries(CONFIG_GROUPS).map(([groupName, group]) => {
            const [gTitle, gDesc] = t(`adminSettings.groups.${groupName}`);
            const Icon = group.icon;
            return (
              <Card key={groupName} className="rounded-2xl border-slate-200 dark:border-slate-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Icon className="w-5 h-5" />{gTitle}</CardTitle>
                  <p className="text-sm text-muted-foreground">{gDesc}</p>
                </CardHeader>
                <CardContent><div className="space-y-4">
                  {group.keys.map((key, idx) => (
                    <React.Fragment key={key}>{idx > 0 && <Separator className="my-2" />}{field(key)}</React.Fragment>
                  ))}
                </div></CardContent>
              </Card>
            );
          })}
          <ApiKeys t={t} />
        </div>
      </div>
    </div>
  );
};

export default AdminConfig;
